/*
 * PostHog analytics for the storefront.
 *
 * Three jobs:
 *
 *   1. Consent. PostHog is initialised in snippets/posthog.liquid with
 *      opt_out_capturing_by_default and cookieless_mode: 'on_reject', so
 *      nothing is stored or sent until Shopify's Customer Privacy API answers.
 *      Analytics allowed: normal tracking with cookies. Not allowed: cookieless
 *      tracking, which stores nothing on the device and counts visits under a
 *      daily anonymous hash (no identity, no attribution storage). If the API
 *      is missing or errors we send nothing at all.
 *
 *   2. Funnel events. add_to_cart and begin_checkout, plus identify() for
 *      logged-in customers. Everything goes through capture() below, which
 *      is a no-op until consent is granted.
 *
 *   3. Checkout hand-off. Shopify's checkout runs on its own domain where this
 *      script cannot load, so purchases are captured by a custom web pixel
 *      (Settings -> Customer events -> "PostHog checkout"). To land those
 *      events on the same PostHog person as the browsing session, we write the
 *      current distinct_id and session id into two hidden cart attributes
 *      (_posthog_distinct_id, _posthog_session_id). The pixel reads them from
 *      checkout.attributes. Underscore-prefixed attributes are not shown to the
 *      customer. We only write when the cart has items and the values changed. On the cart
 *      page the same values are also added to form#cart as hidden inputs, so the
 *      Checkout submit carries them even if the async write has not landed.
 *
 *   4. Attribution. The last campaign click (UTMs or a gclid/fbclid) is kept
 *      for 30 days in localStorage and a first-party pipers_attr cookie, and
 *      sent along with the identity as _utm_* / _click_id cart attributes.
 *      The checkout pixel prefers the cookies (PostHog's own and pipers_attr)
 *      because the delivery-date app can overwrite cart attributes.
 */
(() => {
  const config = window.posthogThemeConfig || {};

  let consentGranted = false;
  // True once the visitor has declined (or not yet accepted) analytics. PostHog
  // then runs in cookieless mode: nothing is stored on the device and visits
  // are counted under a daily anonymous hash computed on PostHog's servers.
  let cookieless = false;
  let identified = false;
  let sessionListenerAttached = false;
  let currentSessionId = null;
  let lastSyncedAttributes = '';

  // Seeded from Liquid at render time, then kept fresh as the cart changes.
  // Used for begin_checkout, which fires as the page navigates away.
  const cartState = {
    itemCount: typeof config.cartItemCount === 'number' ? config.cartItemCount : null,
    totalPrice: typeof config.cartTotalPrice === 'number' ? config.cartTotalPrice : null,
  };

  // True once the loader stub exists. The stub queues calls until array.js loads.
  const posthogReady = () => typeof window.posthog !== 'undefined' && typeof window.posthog.capture === 'function';

  // True only once the real SDK has replaced the stub (the stub has no get_distinct_id).
  const sdkLoaded = () => posthogReady() && typeof window.posthog.get_distinct_id === 'function';

  const capture = (eventName, properties, options) => {
    if ((!consentGranted && !cookieless) || !posthogReady()) return;
    try {
      window.posthog.capture(eventName, properties, options);
    } catch (e) {
      // Never let analytics break the storefront.
    }
  };

  /* --------------------------------------------------------- attribution */

  // Last-touch campaign attribution, carried into checkout as cart attributes
  // so the checkout pixel can put it on purchase events and every Shopify
  // order records which campaign brought it in. Only stored after consent.
  const ATTRIBUTION_KEY = 'pipers_attribution';
  const ATTRIBUTION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000; // matches PostHog's 30-day window
  const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const CLICK_ID_PARAMS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid'];

  const rememberAttribution = () => {
    let params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }

    const touch = {};
    UTM_PARAMS.forEach((name) => {
      const value = params.get(name);
      if (value) touch[name] = value.slice(0, 200);
    });
    CLICK_ID_PARAMS.forEach((name) => {
      const value = params.get(name);
      if (value && !touch.click_id) {
        touch.click_id_type = name;
        touch.click_id = value.slice(0, 200);
      }
    });
    if (!Object.keys(touch).length) return;

    // A click id without UTMs still tells us the platform.
    if (!touch.utm_source && touch.click_id_type) {
      touch.utm_source = touch.click_id_type === 'fbclid' ? 'facebook' : touch.click_id_type === 'msclkid' ? 'bing' : 'google';
      touch.utm_medium = touch.utm_medium || 'paid';
    }
    touch.landing_path = window.location.pathname;
    touch.ts = Date.now();

    try {
      window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(touch));
    } catch (e) {
      // Storage blocked; PostHog's own session properties still work.
    }
  };

  // The checkout pixel reads this first-party cookie directly. Cart attributes
  // alone aren't reliable: the delivery-date app rewrites the cart's attribute
  // list server-side when a date is picked, dropping ours.
  const ATTRIBUTION_COOKIE = 'pipers_attr';

  const writeAttributionCookie = (touch) => {
    try {
      if (!touch) {
        document.cookie = `${ATTRIBUTION_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
        return;
      }
      const payload = {};
      UTM_PARAMS.concat(['click_id_type', 'click_id', 'landing_path', 'ts']).forEach((key) => {
        if (touch[key]) payload[key] = touch[key];
      });
      const maxAge = Math.max(0, Math.round((touch.ts + ATTRIBUTION_MAX_AGE_MS - Date.now()) / 1000));
      document.cookie = `${ATTRIBUTION_COOKIE}=${encodeURIComponent(JSON.stringify(payload))}; path=/; max-age=${maxAge}; SameSite=Lax`;
    } catch (e) {
      // Cookies blocked; cart attributes remain as a fallback.
    }
  };

  const readAttribution = () => {
    try {
      const touch = JSON.parse(window.localStorage.getItem(ATTRIBUTION_KEY) || 'null');
      if (!touch || typeof touch.ts !== 'number') return null;
      if (Date.now() - touch.ts > ATTRIBUTION_MAX_AGE_MS) return null;
      return touch;
    } catch (e) {
      return null;
    }
  };

  // Hidden (underscore-prefixed) cart attributes. Shopify copies these onto
  // the order's note_attributes and exposes them to the checkout pixel.
  const checkoutAttributes = (distinctId, sessionId) => {
    const attributes = {
      _posthog_distinct_id: distinctId,
      _posthog_session_id: sessionId || '',
    };
    const touch = readAttribution();
    UTM_PARAMS.forEach((name) => {
      attributes[`_${name}`] = (touch && touch[name]) || '';
    });
    attributes._click_id_type = (touch && touch.click_id_type) || '';
    attributes._click_id = (touch && touch.click_id) || '';
    attributes._landing_path = (touch && touch.landing_path) || '';
    return attributes;
  };

  /* ------------------------------------------------- checkout hand-off */

  const cartUrl = typeof config.cartUrl === 'string' && config.cartUrl ? config.cartUrl : '/cart';
  const cartUpdateUrl =
    window.routes && typeof window.routes.cart_update_url === 'string'
      ? window.routes.cart_update_url
      : `${cartUrl}/update.js`;

  // Writes the PostHog identity into cart attributes so the checkout pixel can
  // attribute checkout and purchase events to this visitor. Safe to call often:
  // it only sends a request when the cart has items and the values changed.
  const syncCartAttributes = (options) => {
    if (!consentGranted || !sdkLoaded()) return;
    if (cartState.itemCount === 0) return;

    let distinctId = null;
    let sessionId = currentSessionId;
    try {
      distinctId = window.posthog.get_distinct_id();
      if (!sessionId && typeof window.posthog.get_session_id === 'function') {
        sessionId = window.posthog.get_session_id();
      }
    } catch (e) {
      return;
    }
    if (!distinctId) return;

    const attributes = checkoutAttributes(distinctId, sessionId);
    const key = JSON.stringify(attributes);
    if (key === lastSyncedAttributes) return;

    try {
      fetch(cartUpdateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes }),
        // Used from the checkout click handler, where the page is about to navigate.
        keepalive: !!(options && options.keepalive),
      })
        .then((response) => {
          if (response.ok) lastSyncedAttributes = key;
        })
        .catch(() => {
          // Try again on the next cart change.
        });
    } catch (e) {
      // Ignore.
    }
  };

  // Puts the PostHog identity into the cart form as hidden inputs so the
  // checkout submission itself carries the attributes. The cart page's
  // Checkout button is a native submit of form#cart, so this cannot race the
  // async cart update the way a fetch can.
  const stampCartForm = (button) => {
    if (!consentGranted || !sdkLoaded()) return;

    let form = button && button.form ? button.form : null;
    if (!form) form = document.getElementById('cart');
    if (!form || form.tagName !== 'FORM') return;

    let distinctId = null;
    let sessionId = currentSessionId;
    try {
      distinctId = window.posthog.get_distinct_id();
      if (!sessionId && typeof window.posthog.get_session_id === 'function') {
        sessionId = window.posthog.get_session_id();
      }
    } catch (e) {
      return;
    }
    if (!distinctId) return;

    const setHidden = (name, value) => {
      let input = form.querySelector(`input[name="${name}"]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        form.appendChild(input);
      }
      input.value = value;
    };
    const attributes = checkoutAttributes(distinctId, sessionId);
    Object.keys(attributes).forEach((name) => setHidden(`attributes[${name}]`, attributes[name]));
  };

  // array.js loads asynchronously after opt-in, so poll briefly for the real
  // SDK before doing anything that needs get_distinct_id().
  const whenSdkLoaded = (callback) => {
    let attempts = 0;
    const tick = () => {
      if (sdkLoaded()) {
        callback();
        return;
      }
      if (attempts++ >= 100) return; // ~20s, then give up quietly
      setTimeout(tick, 200);
    };
    tick();
  };

  const attachSessionListener = () => {
    if (sessionListenerAttached || !posthogReady()) return;
    if (typeof window.posthog.onSessionId !== 'function') return;
    try {
      // Fires once the SDK has loaded and again whenever the session id rotates.
      window.posthog.onSessionId((sessionId) => {
        currentSessionId = sessionId || null;
        syncCartAttributes();
      });
      sessionListenerAttached = true;
    } catch (e) {
      // Ignore.
    }
  };

  /* ------------------------------------------------------------ identity */

  // Customer id only. No email, name, or any other personal data.
  const identifyCustomer = () => {
    if (identified || !consentGranted || !posthogReady()) return;
    if (config.customerId === null || config.customerId === undefined || config.customerId === '') return;
    try {
      window.posthog.identify(String(config.customerId));
      identified = true;
      // The distinct_id just changed, so the cart attributes are stale.
      lastSyncedAttributes = '';
      syncCartAttributes();
      stampCartForm();
    } catch (e) {
      // Ignore.
    }
  };

  // Opt in quietly. Shopify fires visitorConsentCollected on every page load
  // once consent is stored, so the SDK's default $opt_in event would be sent
  // on every pageview. It carries nothing we analyse, so we never send it.
  const applyConsent = (allowed) => {
    if (!posthogReady()) return;

    if (allowed) {
      if (consentGranted) return;
      try {
        window.posthog.opt_in_capturing({ captureEventName: false });
      } catch (e) {
        return;
      }
      consentGranted = true;
      cookieless = false;
      rememberAttribution();
      writeAttributionCookie(readAttribution());
      identifyCustomer();
      whenSdkLoaded(() => {
        attachSessionListener();
        syncCartAttributes();
        stampCartForm();
      });
      refreshCartState();
      return;
    }

    consentGranted = false;
    identified = false;
    lastSyncedAttributes = '';
    writeAttributionCookie(null);
    try {
      // With cookieless_mode: 'on_reject' (snippets/posthog.liquid) this
      // switches PostHog to cookieless capture rather than stopping it. No
      // identity, attribution storage or cart attributes in this mode.
      window.posthog.opt_out_capturing();
      cookieless = true;
    } catch (e) {
      // Ignore.
    }
  };

  // analyticsProcessingAllowed() combines the merchant's privacy settings, the
  // visitor's region and the consent they gave, so it is the value to trust.
  const analyticsAllowed = () => {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return false;
    try {
      return privacy.analyticsProcessingAllowed() === true;
    } catch (e) {
      return false;
    }
  };

  // Consent can be given or withdrawn after load; take effect without a reload.
  document.addEventListener('visitorConsentCollected', (event) => {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') {
      applyConsent(analyticsAllowed());
    } else {
      applyConsent(!!(event && event.detail && event.detail.analyticsAllowed));
    }
  });

  const initConsent = () => {
    // Fail safe: without the consent API we never opt in.
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

    try {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (error) return;
        applyConsent(analyticsAllowed());
      });
    } catch (e) {
      // Stay opted out.
    }
  };

  /* ---------------------------------------------------------------- cart */

  const toMoney = (cents) => (typeof cents === 'number' ? Math.round(cents) / 100 : null);

  const isFullCart = (data) =>
    !!data && typeof data.item_count === 'number' && typeof data.total_price === 'number';

  let refreshTimer = null;

  const refreshCartState = () => {
    if (!consentGranted && !cookieless) return;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          if (isFullCart(cart)) {
            cartState.itemCount = cart.item_count;
            cartState.totalPrice = cart.total_price;
          }
          syncCartAttributes();
        })
        .catch(() => {
          // Keep the last known values.
        });
    }, 250);
  };

  /* -------------------------------------------------------- add_to_cart */

  // The theme publishes cart-update for quantity edits as well as adds, so we
  // only act on the product-form source. That covers both the product page
  // form and the quick-add bottom sheet (which renders the same element).
  const addSource = (payload) => {
    if (payload.addContext === 'quick-add') return 'quick_add';
    return config.templateName === 'product' ? 'product_page' : 'other';
  };

  const captureAddToCart = (payload) => {
    const data = payload && payload.cartData;
    if (!data) return;

    // A single add returns the line item; a bundled add returns { items: [...] }.
    const lines = Array.isArray(data.items) ? data.items : [data];
    const source = addSource(payload);

    lines.forEach((line) => {
      if (!line || (!line.variant_id && !line.id)) return;

      capture('add_to_cart', {
        product_id: line.product_id !== undefined ? String(line.product_id) : null,
        variant_id: String(line.variant_id !== undefined ? line.variant_id : line.id),
        product_title: line.product_title || line.title || null,
        variant_title: line.variant_title || null,
        price: toMoney(line.price),
        quantity: typeof line.quantity === 'number' ? line.quantity : null,
        currency: config.currency || null,
        source,
      });
    });
  };

  /* ----------------------------------------------------- begin_checkout */

  // The cart drawer and the accelerated checkout buttons are intercepted by
  // global.js and redirect to the cart page, so the cart page button is the
  // only real entry to checkout in this theme.
  const CHECKOUT_SELECTOR = 'button[name="checkout"], #checkout, [data-checkout-button]';

  document.addEventListener(
    'click',
    (event) => {
      if (!consentGranted && !cookieless) return;
      if (!event.target || typeof event.target.closest !== 'function') return;

      const button = event.target.closest(CHECKOUT_SELECTOR);
      if (!button) return;
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

      // Make sure checkout carries the PostHog identity. The hidden inputs ride
      // along with this very submit; the fetch is a fallback for other entry
      // points that do not go through form#cart.
      stampCartForm(button);
      syncCartAttributes({ keepalive: true });

      capture(
        'begin_checkout',
        {
          value: toMoney(cartState.totalPrice),
          currency: config.currency || null,
          item_count: cartState.itemCount,
        },
        // The page navigates immediately after this click.
        { send_instantly: true }
      );
    },
    true
  );

  /* ------------------------------------------------------------- wiring */

  // constants.js declares PUB_SUB_EVENTS with const, so it lives in the global
  // lexical scope rather than on window. Reference the bare identifiers.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (payload) => {
      if (!payload) return;

      if (isFullCart(payload.cartData)) {
        cartState.itemCount = payload.cartData.item_count;
        cartState.totalPrice = payload.cartData.total_price;
        // A product-form add returns the line item, not the full cart, so the
        // cart is known to be non-empty either way.
      } else {
        if (payload.source === 'product-form' && cartState.itemCount === 0) cartState.itemCount = null;
        refreshCartState();
      }

      if (payload.source === 'product-form') captureAddToCart(payload);
      syncCartAttributes();
      stampCartForm();
    });
  }

  initConsent();
})();